import {
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { FieldProps } from 'formik';
import { Eye, EyeOff } from 'lucide-react';

// DS focus ring (design-guidelines.md §4: input focus → border primary + ring)
const dsFieldFocus = {
  borderColor: 'primary.solid',
  boxShadow: '0 0 0 3px var(--chakra-colors-primary-soft)',
};

interface PasswordFieldProps {
  field: FieldProps['field'];
  id: string;
  label: string;
  show: boolean;
  onToggle: () => void;
  toggleLabel: string;
}

// Markup-only DS password input (reskin of Chakra Input + show/hide toggle).
const PasswordField = ({ field, id, label, show, onToggle, toggleLabel }: PasswordFieldProps) => (
  <>
    <FormLabel mb={1.5} color='text.default' fontSize='sm' fontWeight={500}>
      {label}
    </FormLabel>
    <InputGroup>
      <Input
        {...field}
        h='40px'
        px={3}
        color='text.default'
        fontSize='md'
        // Distinct inset bg: the card is bg.surface, so an input on bg.surface is invisible in
        // dark mode (same color). bg.canvas insets it visibly in both themes. (#77945-3)
        bg='bg.canvas'
        borderWidth='1px'
        borderColor='border.strong'
        borderRadius='md'
        _hover={{ borderColor: 'primary.solid' }}
        _focus={dsFieldFocus}
        _focusVisible={dsFieldFocus}
        _placeholder={{ color: 'text.subtle' }}
        transition='border-color 0.16s, box-shadow 0.16s'
        id={id}
        placeholder=''
        type={show ? 'text' : 'password'}
        variant='unstyled'
      />
      <InputRightElement h='40px'>
        <IconButton
          color='text.muted'
          _hover={{ bg: 'primary.soft', color: 'primary.solid' }}
          aria-label={toggleLabel}
          icon={<Icon as={show ? EyeOff : Eye} boxSize='18px' />}
          onClick={onToggle}
          size='sm'
          variant='ghost'
        />
      </InputRightElement>
    </InputGroup>
  </>
);

export default PasswordField;
